/**
 * Consentimiento de cookies y carga de Google Tag Manager.
 *
 * Regla única de este fichero: NADA de terceros toca el navegador hasta que
 * alguien pulsa "aceptar". No se precarga `gtm.js`, no se abre conexión, no se
 * escribe una cookie. El art. 5.3 de la Directiva ePrivacy (art. 22.2 de la
 * LSSI en España) exige consentimiento PREVIO, y "previo" no admite matices:
 * un contenedor que se descarga mientras el banner esta en pantalla ya ha
 * hecho la transferencia que se estaba pidiendo permiso para hacer.
 *
 * FALLO SEGURO AL REVES QUE EL RESTO DE LA PAGINA
 * -----------------------------------------------
 * `status.ts` falla hacia "no lo se" porque esconder información es peor que
 * darla incompleta. Aquí es al contrario: cualquier fallo —localStorage que
 * lanza, JSON corrupto, JavaScript que no llega a ejecutarse— acaba en NO
 * CARGAR NADA. La duda no se resuelve a favor de la analitica.
 *
 * POR QUE NO HAY BLOQUE `<noscript>`
 * ----------------------------------
 * El fragmento que da Google incluye un `<iframe>` a `ns.html` para navegadores
 * sin JavaScript. Esta deliberadamente omitido: sin JavaScript no hay banner,
 * así que no hay forma de recabar consentimiento, y ese iframe dispararia las
 * etiquetas igualmente. Es exactamente el caso que la norma prohibe. La
 * consecuencia es que no hay analitica de visitas sin JavaScript, que es el
 * precio correcto.
 *
 * QUE SE GUARDA SIN PERMISO
 * -------------------------
 * Solo la decisión, en `localStorage`. Es la excepción de "estrictamente
 * necesario": sin guardarla no se puede respetar, y volver a preguntar en cada
 * carga es peor para quien ya dijo que no.
 */

/** Formato de contenedor de GTM. Se valida porque el valor llega de una `.env`. */
const FORMATO_GTM = /^GTM-[A-Z0-9]{4,12}$/

const GTM = String(import.meta.env.VITE_GTM_ID ?? '').trim()

const CLAVE = 'rizzlup.cookies'

type Decision = 'aceptado' | 'rechazado'

/**
 * Las seis señales del Consent Mode v2 mas `security_storage`. Se declaran
 * todas en `denied` ANTES de cargar nada; si mañana alguien añade una etiqueta
 * de Ads desde el panel de GTM, nace denegada en vez de nacer activa.
 */
const SEÑALES = [
  'ad_storage',
  'ad_user_data',
  'ad_personalization',
  'analytics_storage',
  'functionality_storage',
  'personalization_storage',
] as const

declare global {
  interface Window {
    dataLayer?: unknown[]
  }
}

const banner = document.querySelector<HTMLElement>('[data-cookies]')

function empujar(...argumentos: unknown[]): void {
  window.dataLayer = window.dataLayer ?? []
  // `arguments` de verdad y no un array: `gtag()` mira `arguments` con
  // `Array.prototype.slice`, y un array normal se serializa distinto. Es la
  // única forma de hablar con el Consent Mode desde código propio.
  window.dataLayer.push(argumentos)
}

function consentimiento(estado: 'denied' | 'granted'): void {
  const señales = Object.fromEntries(SEÑALES.map((s) => [s, estado]))
  empujar('consent', estado === 'denied' ? 'default' : 'update', {
    ...señales,
    // La única que va concedida siempre: cubre lo imprescindible para detectar
    // fraude y esta exenta de consentimiento.
    security_storage: 'granted',
  })
}

function leer(): Decision | null {
  // `localStorage` lanza en navegación privada de algunos navegadores y con el
  // almacenamiento de sitio bloqueado. Se trata como "no ha decidido".
  try {
    const valor = localStorage.getItem(CLAVE)
    return valor === 'aceptado' || valor === 'rechazado' ? valor : null
  } catch {
    return null
  }
}

function guardar(decision: Decision): void {
  try {
    localStorage.setItem(CLAVE, decision)
  } catch {
    // Si no se puede guardar, la decisión vale para esta página y se volverá a
    // preguntar en la siguiente. Molesto, pero es lo único honesto que se puede
    // hacer sin escribir en un sitio donde no nos dejan.
  }
}

function cargarGtm(): void {
  if (document.getElementById('gtm')) return

  empujar({ 'gtm.start': Date.now(), event: 'gtm.js' })

  const script = document.createElement('script')
  script.id = 'gtm'
  // `async`: el contenedor no puede retrasar el pintado de la página, y menos
  // el del botón JUGAR (regla 2).
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM)}`
  document.head.appendChild(script)
}

function ocultar(): void {
  if (banner) banner.hidden = true
}

function mostrar(): void {
  if (banner) banner.hidden = false
}

function decidir(decision: Decision): void {
  guardar(decision)
  ocultar()

  if (decision !== 'aceptado') return
  consentimiento('granted')
  cargarGtm()
}

function iniciar(): void {
  // Sin contenedor configurado no hay cookies de terceros, así que no hay nada
  // que consentir: enseñar un banner seria pedir permiso para nada. Es el caso
  // de un `npm run dev` suelto y el de cualquier entorno sin `GTM_ID`.
  if (!FORMATO_GTM.test(GTM)) {
    ocultar()
    return
  }

  // Denegado por defecto y antes de nada. Esta línea tiene que ejecutarse aunque
  // el banner no exista en el DOM.
  consentimiento('denied')

  // Punto de retirada: el art. 7.3 del RGPD exige que retirar el consentimiento
  // sea tan fácil como darlo. Vive en la política de cookies.
  document.querySelectorAll<HTMLElement>('[data-cookies-cambiar]').forEach((boton) => {
    boton.hidden = false
    boton.addEventListener('click', () => {
      try {
        localStorage.removeItem(CLAVE)
      } catch {
        // Si no se pudo guardar tampoco hay nada que borrar.
      }
      mostrar()
    })
  })

  const decision = leer()

  if (decision === 'aceptado') {
    consentimiento('granted')
    cargarGtm()
    ocultar()
    return
  }

  if (decision === 'rechazado') {
    ocultar()
    return
  }

  banner
    ?.querySelectorAll<HTMLElement>('[data-cookies-decision]')
    .forEach((boton) => {
      const valor = boton.dataset.cookiesDecision
      if (valor !== 'aceptado' && valor !== 'rechazado') return
      boton.addEventListener('click', () => decidir(valor))
    })

  mostrar()
}

iniciar()
